import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Font,
  Svg,
  Rect,
  Line,
  Circle,
  Path,
  Polyline,
  Image,
} from "@react-pdf/renderer";
import Html from "react-pdf-html";
import { ServiceOrderWithTasks } from '../../types/serviceOrder';
import { TaskAreaLabels } from '../../types/task';

/* ── Disable hyphenation ──────────────────────────────────────────── */
Font.registerHyphenationCallback(word => [word]);

/* ── Design tokens ────────────────────────────────────────────────── */
const t = {
  primary: "#1a1a2e",
  accent: "#4361ee",
  accentLight: "#eef1ff",
  text: "#2d2d3a",
  textSecondary: "#6b7280",
  border: "#e5e7eb",
  bg: "#f8f9fc",
  white: "#ffffff",
  green: "#059669",
  red: "#dc2626",
  purple: "#8b5cf6",
  orange: "#f59e0b",
};

/* ── Area colors ──────────────────────────────────────────────────── */
const areaColors: Record<string, string> = {
  frontend: t.accent,
  backend: t.green,
  design: t.purple,
  'Q&A': t.orange,
};

/* ── HTML stylesheet for rich text ────────────────────────────────── */
const htmlStyles = {
  p: {
    fontSize: 6,
    color: t.textSecondary,
    marginTop: 2,
    marginBottom: 0,
    lineHeight: 1.3,
  },
  strong: {
    fontFamily: "Helvetica-Bold",
  },
  b: {
    fontFamily: "Helvetica-Bold",
  },
  em: {
    fontStyle: "italic",
  },
  i: {
    fontStyle: "italic",
  },
  a: {
    color: t.accent,
    textDecoration: "none",
  },
  ul: {
    marginTop: 2,
    marginBottom: 0,
    paddingLeft: 10,
  },
  ol: {
    marginTop: 2,
    marginBottom: 0,
    paddingLeft: 10,
  },
  li: {
    fontSize: 6,
    color: t.textSecondary,
    marginBottom: 1,
  },
  span: {
    fontSize: 6,
    color: t.textSecondary,
  },
};

/* ── Styles ───────────────────────────────────────────────────────── */
const s = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    backgroundColor: t.bg,
    padding: "28 32",
    color: t.text,
  },
  /* Header card */
  header: {
    backgroundColor: t.white,
    border: `1 solid ${t.border}`,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  headerTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  headerTopLeft: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  headerLogo: {
    height: 28,
  },
  headerLabel: {
    fontSize: 5,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 1.2,
    color: t.accent,
    backgroundColor: t.accentLight,
    padding: "2 6",
    borderRadius: 3,
  },
  periodTag: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  periodDates: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: t.primary,
  },
  headerTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: t.primary,
    marginBottom: 2,
  },
  headerSubtitle: {
    fontSize: 7,
    color: t.textSecondary,
    lineHeight: 1.3,
  },
  /* Summary cards */
  summaryGrid: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 14,
  },
  summaryCard: {
    flex: 1,
    backgroundColor: t.white,
    border: `1 solid ${t.border}`,
    borderRadius: 6,
    padding: 8,
  },
  summaryLabel: {
    fontSize: 5,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.6,
    color: t.textSecondary,
    marginBottom: 2,
  },
  summaryValueRow: {
    flexDirection: "row",
    alignItems: "baseline",
  },
  summaryValue: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: t.primary,
  },
  summaryValueSmall: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: t.primary,
  },
  summaryUnit: {
    fontSize: 6,
    color: t.textSecondary,
    marginLeft: 2,
  },
  summaryDetail: {
    fontSize: 5,
    color: t.textSecondary,
    marginTop: 2,
  },
  /* Hours distribution */
  distribution: {
    backgroundColor: t.white,
    border: `1 solid ${t.border}`,
    borderRadius: 8,
    padding: 12,
    marginBottom: 14,
  },
  distributionTitle: {
    fontSize: 6,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.8,
    color: t.textSecondary,
    marginBottom: 8,
  },
  barContainer: {
    flexDirection: "row",
    height: 8,
    borderRadius: 4,
    overflow: "hidden",
    backgroundColor: "#f1f3f5",
  },
  barLegend: {
    flexDirection: "row",
    gap: 14,
    marginTop: 6,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  legendDot: {
    width: 6,
    height: 6,
    borderRadius: 2,
  },
  legendText: {
    fontSize: 7,
    color: t.textSecondary,
  },
  legendHours: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: t.text,
    marginLeft: 1,
  },
  /* Section */
  section: {
    marginBottom: 10,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  sectionBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: t.primary,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  sectionBadgeText: {
    fontSize: 5,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    color: t.white,
  },
  sectionLine: {
    flex: 1,
    height: 1,
    backgroundColor: t.border,
  },
  /* Task card */
  taskList: {
    gap: 3,
  },
  taskCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: t.white,
    border: `1 solid ${t.border}`,
    borderRadius: 4,
    padding: "5 8",
  },
  taskLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flex: 1,
  },
  taskNumber: {
    width: 14,
    height: 14,
    borderRadius: 3,
    backgroundColor: t.bg,
    alignItems: "center",
    justifyContent: "center",
  },
  taskNumberText: {
    fontSize: 5,
    fontFamily: "Helvetica-Bold",
    color: t.textSecondary,
  },
  taskContent: {
    flex: 1,
  },
  taskName: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: t.text,
  },
  taskDescription: {
    fontSize: 6,
    color: t.textSecondary,
    marginTop: 2,
    lineHeight: 1.3,
  },
  taskHours: {
    fontSize: 6,
    fontFamily: "Helvetica-Bold",
    color: t.primary,
    backgroundColor: t.bg,
    padding: "2 6",
    borderRadius: 3,
    marginLeft: 6,
  },
  /* Area total */
  areaTotal: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: 4,
    paddingTop: 4,
    borderTop: `1 solid ${t.border}`,
  },
  areaTotalLabel: {
    fontSize: 5,
    color: t.textSecondary,
    marginRight: 6,
  },
  areaTotalValue: {
    fontSize: 6,
    fontFamily: "Helvetica-Bold",
    color: t.primary,
    backgroundColor: t.bg,
    padding: "2 8",
    borderRadius: 3,
  },
  /* Footer */
  footer: {
    marginTop: "auto",
    paddingTop: 10,
    borderTop: `1 solid ${t.border}`,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerLeft: {
    fontSize: 5,
    color: t.textSecondary,
  },
  footerBrand: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  footerName: {
    fontSize: 6,
    fontFamily: "Helvetica-Bold",
    color: t.textSecondary,
  },
});

/* ── SVG Icons ────────────────────────────────────────────────────── */
const CalendarIcon = () => (
  <Svg width={9} height={9} viewBox="0 0 24 24">
    <Rect x={3} y={4} width={18} height={18} rx={2} ry={2} stroke={t.textSecondary} strokeWidth={2} fill="none" />
    <Line x1={16} y1={2} x2={16} y2={6} stroke={t.textSecondary} strokeWidth={2} />
    <Line x1={8} y1={2} x2={8} y2={6} stroke={t.textSecondary} strokeWidth={2} />
    <Line x1={3} y1={10} x2={21} y2={10} stroke={t.textSecondary} strokeWidth={2} />
  </Svg>
);

const CodeIcon = () => (
  <Svg width={7} height={7} viewBox="0 0 24 24">
    <Polyline points="16,18 22,12 16,6" stroke={t.white} strokeWidth={2.5} fill="none" />
    <Polyline points="8,6 2,12 8,18" stroke={t.white} strokeWidth={2.5} fill="none" />
  </Svg>
);

const ServerIcon = () => (
  <Svg width={7} height={7} viewBox="0 0 24 24">
    <Rect x={2} y={2} width={20} height={8} rx={2} ry={2} stroke={t.white} strokeWidth={2.5} fill="none" />
    <Rect x={2} y={14} width={20} height={8} rx={2} ry={2} stroke={t.white} strokeWidth={2.5} fill="none" />
    <Circle cx={6} cy={6} r={1} fill={t.white} />
    <Circle cx={6} cy={18} r={1} fill={t.white} />
  </Svg>
);

const PenIcon = () => (
  <Svg width={7} height={7} viewBox="0 0 24 24">
    <Path d="M12 19l7-7 3 3-7 7-3-3z" stroke={t.white} strokeWidth={2.5} fill="none" />
    <Path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" stroke={t.white} strokeWidth={2.5} fill="none" />
  </Svg>
);

const BugIcon = () => (
  <Svg width={7} height={7} viewBox="0 0 24 24">
    <Circle cx={12} cy={12} r={8} stroke={t.white} strokeWidth={2.5} fill="none" />
    <Path d="M12 8v4l2 2" stroke={t.white} strokeWidth={2.5} fill="none" />
  </Svg>
);

/* ── Area icon mapping ────────────────────────────────────────────── */
const areaIcons: Record<string, React.FC> = {
  frontend: CodeIcon,
  backend: ServerIcon,
  design: PenIcon,
  'Q&A': BugIcon,
};

/* ── Props interface ──────────────────────────────────────────────── */
interface ServiceOrderPDFProps {
  serviceOrder: ServiceOrderWithTasks;
  projectName: string;
  projectLogo?: string;
}

/* ── Main Document ────────────────────────────────────────────────── */
const ServiceOrderPDF: React.FC<ServiceOrderPDFProps> = ({
  serviceOrder,
  projectName,
  projectLogo,
}) => {
  // Format date helper - DD/MM/YY format
  const formatDateShort = (dateString: string | undefined | null): string => {
    if (!dateString) return '--/--/--';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '--/--/--';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch {
      return '--/--/--';
    }
  };

  // Calculate totals with safe defaults
  const totalHours = serviceOrder?.totalHoras || 0;
  const totalTasks = serviceOrder?.totalTasks || 0;
  const avgHoursPerTask = totalTasks > 0 ? Math.round(totalHours / totalTasks) : 0;

  // Get sorted areas with safety checks
  const tasksByArea = serviceOrder?.tasksByArea || {};
  const sortedAreas = Object.keys(tasksByArea).filter(area => {
    const areaData = tasksByArea[area as keyof typeof tasksByArea];
    return areaData && areaData.tasks && areaData.tasks.length > 0;
  }).sort((a, b) => {
    const aData = tasksByArea[a as keyof typeof tasksByArea];
    const bData = tasksByArea[b as keyof typeof tasksByArea];
    return (bData?.totalHoras || 0) - (aData?.totalHoras || 0);
  });

  // Calculate min/max hours with safety checks
  const allTasks = sortedAreas.flatMap(area => {
    const areaData = tasksByArea[area as keyof typeof tasksByArea];
    return (areaData?.tasks || []).filter(t => t && typeof t.horasEstimadas === 'number');
  });
  const taskHours = allTasks.map(t => t.horasEstimadas);
  const minHours = taskHours.length > 0 ? Math.min(...taskHours) : 0;
  const maxHours = taskHours.length > 0 ? Math.max(...taskHours) : 0;

  // Build distribution data
  const distributionData = sortedAreas.map(area => {
    const areaData = tasksByArea[area as keyof typeof tasksByArea];
    const hours = areaData?.totalHoras || 0;
    const pctNumber = totalHours > 0 ? (hours / totalHours) * 100 : 0;
    return {
      area,
      label: TaskAreaLabels[area as keyof typeof TaskAreaLabels] || area,
      hours,
      pctNumber, // numeric percentage for flex calculation
      pctLabel: `${pctNumber.toFixed(1)}%`, // string for display
      color: areaColors[area] || t.textSecondary,
    };
  });

  // Build task counter
  let taskCounter = 0;

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* Header */}
        <View style={s.header}>
          <View style={s.headerTop}>
            <View style={s.headerTopLeft}>
              {projectLogo && (
                <Image src={projectLogo} style={s.headerLogo} />
              )}
            </View>
            <View style={s.periodTag}>
              <CalendarIcon />
              <Text style={s.periodDates}>
                {formatDateShort(serviceOrder.startDate)} a {formatDateShort(serviceOrder.endDate)}
              </Text>
            </View>
          </View>
          <Text style={s.headerTitle}>{serviceOrder?.name || 'Ordem de Serviço'}</Text>
          {serviceOrder?.description && (
            <Text style={s.headerSubtitle}>{serviceOrder.description}</Text>
          )}
        </View>

        {/* Summary cards */}
        <View style={s.summaryGrid}>
          <View style={s.summaryCard}>
            <Text style={s.summaryLabel}>TOTAL DE HORAS</Text>
            <View style={s.summaryValueRow}>
              <Text style={s.summaryValue}>{totalHours}</Text>
              <Text style={s.summaryUnit}>horas</Text>
            </View>
            <Text style={s.summaryDetail}>{totalTasks} tarefas estimadas</Text>
          </View>
          <View style={s.summaryCard}>
            <Text style={s.summaryLabel}>FRENTES</Text>
            <View style={s.summaryValueRow}>
              <Text style={s.summaryValue}>{sortedAreas.length}</Text>
              <Text style={s.summaryUnit}>áreas</Text>
            </View>
            <Text style={s.summaryDetail}>
              {distributionData.map(d => d.label).join(' · ')}
            </Text>
          </View>
          <View style={s.summaryCard}>
            <Text style={s.summaryLabel}>MÉDIA POR TAREFA</Text>
            <View style={s.summaryValueRow}>
              <Text style={s.summaryValue}>{avgHoursPerTask}</Text>
              <Text style={s.summaryUnit}>h/tarefa</Text>
            </View>
            <Text style={s.summaryDetail}>Variação: {minHours}h – {maxHours}h</Text>
          </View>
        </View>

        {/* Task sections */}
        {sortedAreas.map((area) => {
          const areaKey = area as keyof typeof tasksByArea;
          const areaData = tasksByArea[areaKey];
          if (!areaData || !areaData.tasks || areaData.tasks.length === 0) return null;

          const AreaIcon = areaIcons[area] || CodeIcon;
          const areaLabel = TaskAreaLabels[areaKey] || area;

          return (
            <View key={area} style={s.section}>
              <View style={s.sectionHeader}>
                <View style={[s.sectionBadge, { backgroundColor: areaColors[area] || t.primary }]}>
                  <AreaIcon />
                  <Text style={s.sectionBadgeText}>{areaLabel.toUpperCase()}</Text>
                </View>
                <View style={s.sectionLine} />
              </View>
              <View style={s.taskList}>
                {areaData.tasks.filter(task => task && task._id).map((task, index) => {
                  taskCounter++;
                  const taskNum = String(taskCounter).padStart(2, '0');
                  return (
                    <View key={task._id || `task-${index}`} style={s.taskCard}>
                      <View style={s.taskLeft}>
                        <View style={s.taskNumber}>
                          <Text style={s.taskNumberText}>{taskNum}</Text>
                        </View>
                        <View style={s.taskContent}>
                          <Text style={s.taskName}>{task.name || 'Tarefa sem nome'}</Text>
                          {task.description && (
                            <Html stylesheet={htmlStyles}>{task.description}</Html>
                          )}
                        </View>
                      </View>
                      <Text style={s.taskHours}>{task.horasEstimadas || 0}h</Text>
                    </View>
                  );
                })}
              </View>
              {/* Area total */}
              <View style={s.areaTotal}>
                <Text style={s.areaTotalLabel}>Total {areaLabel}:</Text>
                <Text style={s.areaTotalValue}>{areaData.totalHoras || 0}h</Text>
              </View>
            </View>
          );
        })}

        {/* Footer */}
        <View style={s.footer}>
          <Text style={s.footerLeft}>
            Gerado em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}
          </Text>
          <View style={s.footerBrand}>
            <Svg width={7} height={7} viewBox="0 0 8 8">
              <Circle cx={4} cy={4} r={4} fill={t.accent} />
            </Svg>
            <Text style={s.footerName}>{projectName}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ServiceOrderPDF;
